import React, { useCallback, useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { ClientDto } from 'api/Clients/Types/Client'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { RestrictionDto } from 'api/Wallet/Types/RestrictionDto'
import { RestrictionTypes } from 'api/Wallet/Types/RestrictionTypes'
import { TradingAccountToWalletTransfer } from 'api/Wallet/Types/TradingAccountToWalletTransfer'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import { ToastContext } from 'components/Toast/Toast'
import { getIdFromLocationHeader } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { WalletsApiClient } from 'utils/api/walletsApiClient'

import TradingAccountToWalletTransferForm from './TradingAccountToWalletTransferForm'

type TradingAccountToWalletTransferProps = {
  client: ClientDto | null
  predefinedTradingAccount?: TradingAccountDto
  preSelectedWallet?: WalletDto

  close?: () => void
  setClient?: () => void
}

const selectTradingAccountById = (
  tradingAccountItems: TradingAccountDto[],
  tradingAccountId: string
): TradingAccountDto | undefined => {
  return tradingAccountItems.find((x) => x.id === tradingAccountId)
}

const filterFromRestrictions = (restriction: RestrictionDto) =>
  ![RestrictionTypes.TransferTo].includes(restriction.id)
const filterToRestrictions = (restriction: RestrictionDto) =>
  ![RestrictionTypes.TransferFrom].includes(restriction.id)

const useFormatter = (props: {
  selectedWallet?: WalletDto
  selectedTradingAccount?: TradingAccountDto
}) => {
  const fromRestrictions =
    props?.selectedTradingAccount?.restrictions.filter(filterFromRestrictions) || []
  const toRestrictions = props?.selectedWallet?.restrictions.filter(filterToRestrictions) || []
  return {
    tradingAccountTooltipTitle: fromRestrictions.map((restriction) => restriction.name).join(', '),
    hasTradingAccountRestrictions: fromRestrictions?.length > 0,
    walletTooltipTitle: toRestrictions?.map((restriction) => restriction.name).join(', '),
    hasWalletRestrictions: toRestrictions?.length > 0,
  }
}

const TradingAccountToWalletTransferPage: React.FC<TradingAccountToWalletTransferProps> = (
  props
) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [isLoading, setLoading] = useState<boolean>(false)
  const [currency, setCurrency] = useState<
    null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  >(null)

  const [tradingAccounts, setTradingAccounts] = useState<TradingAccountDto[]>([])
  const [selectedWallet, setSelectedWallet] = useState<WalletDto>()
  const [selectedTradingAccount, setSelectedTradingAccount] = useState<TradingAccountDto>()

  const setToast = useContext(ToastContext)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const clientId = props?.client?.id

  const fetchTradingAccounts = useCallback(async () => {
    if (clientId) {
      const result = await tradingAccountsApiClient.getTradingAccountByClientId(clientId, {
        pageIndex: 1,
        pageSize: 100,
      })
      setTradingAccounts(result.items)
    }
  }, [clientId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (clientId) {
      const fetchData = async () => {
        try {
          setLoading(true)
          await fetchTradingAccounts()
          return setLoading(false)
        } catch (error) {
          console.error('Cannot get trading accounts list', error)
          return setLoading(false)
        }
      }
      fetchData()
    }
  }, [clientId, fetchTradingAccounts])

  useEffect(() => {
    if (props.predefinedTradingAccount) {
      onSelectWalletForTradingAccount(props.predefinedTradingAccount.id)
    }
  }, [props.predefinedTradingAccount, tradingAccounts]) // eslint-disable-line react-hooks/exhaustive-deps

  const {
    tradingAccountTooltipTitle,
    walletTooltipTitle,
    hasWalletRestrictions,
    hasTradingAccountRestrictions,
  } = useFormatter({
    selectedWallet,
    selectedTradingAccount,
  })

  const onSubmit = async (values: TradingAccountToWalletTransfer) => {
    const response = await walletsApiClient.createTradingAccountToWalletTransfer(values)
    if (response) {
      setToast({
        type: 'success',
        requireInteraction: true,
        title: 'Transaction has been submitted.',
        subtitle: 'Click for details',
        url: generatePath('/transactions/:id', {
          id: getIdFromLocationHeader(response.headers.location),
        }),
      })
    }
    await fetchTradingAccounts()

    if (props.close) {
      props.close()
    }
    return response
  }

  const onSelectWalletForTradingAccount = async (tradingAccountId: string) => {
    if (!tradingAccounts) {
      return null
    }
    const matchTradingAccount = selectTradingAccountById(tradingAccounts, tradingAccountId)
    if (matchTradingAccount) {
      try {
        setLoading(true)
        const wallet = await walletsApiClient.getWalletById(matchTradingAccount.wallet.id)
        setSelectedTradingAccount(matchTradingAccount)
        setSelectedWallet(wallet)
        setCurrency(wallet.currency.id)
        setLoading(false)
      } catch (error) {
        console.error('Cannot get wallet', error)
        setLoading(false)
      }
    }
  }

  const resetAll = () => {
    setSelectedWallet(undefined)
    setSelectedTradingAccount(undefined)
  }

  return (
    <TradingAccountToWalletTransferForm
      {...props}
      isLoading={isLoading}
      currency={currency}
      onSelectWalletForTradingAccount={onSelectWalletForTradingAccount}
      tradingAccounts={tradingAccounts}
      selectedWallet={selectedWallet}
      selectedTradingAccount={selectedTradingAccount}
      tradingAccountTooltipTitle={tradingAccountTooltipTitle}
      walletTooltipTitle={walletTooltipTitle}
      hasWalletRestrictions={hasWalletRestrictions}
      hasTradingAccountRestrictions={hasTradingAccountRestrictions}
      resetAll={resetAll}
      onSubmit={onSubmit}
    />
  )
}

export default TradingAccountToWalletTransferPage
